/**
 * Analytics data transformation utilities
 * Transforms backend row-form data to chart-ready pivoted format
 *
 * Month handling is strictly string-based (YYYY-MM) to avoid timezone drift:
 * - Backend sends "2026-01"; UI must show "Jan 2026" never "Dec 2025"
 * - Do NOT use Date, getFullYear, getMonth, or toLocaleDateString for month keys/labels
 */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Normalize channel code for lookup (lowercase, trim). */
function normalizeChannelCode(v) {
  return (v || '').toString().trim().toLowerCase();
}

/** Master code -> display name. Ensures we always store under canonical names matching charts. */
const MASTER_CODE_TO_NAME = { airbnb: 'Airbnb', vrbo: 'VRBO', clc: 'CLC', owner: 'Owner' };

/** Resolve row.channel_code to canonical channel name for storage. Uses channels list first, then master-code fallback. */
function resolveChannelName(row, channels) {
  const raw = normalizeChannelCode(row.channel_code);
  const ch = channels.find((c) => normalizeChannelCode(c.code) === raw);
  if (ch && ch.name) return ch.name;
  return MASTER_CODE_TO_NAME[raw] || row.channel_code || '';
}

/**
 * Parse YYYY-MM or YYYY-MM-DD into [year, month] without Date (timezone-safe)
 * @param {string} s
 * @returns {{ year: number, month: number }|null}
 */
function parseYearMonth(s) {
  if (typeof s !== 'string' || !s) return null;
  const part = s.split('T')[0];
  const parts = part.split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return null;
  return { year: y, month: m };
}

/**
 * Normalize month to a stable ISO date string (YYYY-MM-01)
 * Uses string parsing only for YYYY-MM / YYYY-MM-DD to avoid timezone drift.
 * @param {string|Date} month - Backend "YYYY-MM" or "YYYY-MM-DD", or legacy Date
 * @returns {string} - YYYY-MM-01 format for stable month key
 */
export function normalizeMonthKey(month) {
  if (typeof month === 'string') {
    const parsed = parseYearMonth(month);
    if (parsed) {
      return `${parsed.year}-${String(parsed.month).padStart(2, '0')}-01`;
    }
  }
  if (month instanceof Date && !isNaN(month.getTime())) {
    const y = month.getFullYear();
    const m = month.getMonth() + 1;
    return `${y}-${String(m).padStart(2, '0')}-01`;
  }
  console.warn('[analyticsTransform] Invalid month value:', month);
  return '1970-01-01';
}

/**
 * Format month key for display (e.g., "Jan 2026")
 * Uses string parsing + MONTH_NAMES only; no Date/toLocaleDateString (timezone-safe).
 * @param {string} monthKey - YYYY-MM-01 or YYYY-MM
 * @returns {string} - Display-friendly format
 */
export function formatMonthForDisplay(monthKey) {
  const parsed = parseYearMonth(monthKey);
  if (!parsed) return monthKey;
  return `${MONTH_NAMES[parsed.month - 1]} ${parsed.year}`;
}

/**
 * Transform revenue_by_channel rows to chart data
 * @param {Array} revenueRows - Array of { month, channel_code, total_revenue }
 * @param {Array} channels - Array of { code, name, color_hex }
 * @returns {Array} - Chart data: [{ month, monthKey, channel1: val, channel2: val, ... }, ...]
 */
export function transformRevenueData(revenueRows, channels = []) {
  if (!revenueRows || revenueRows.length === 0) {
    return [];
  }

  // Build map of month -> { channel: value }
  const monthsMap = {};
  
  revenueRows.forEach((row) => {
    const monthKey = normalizeMonthKey(row.month);
    
    if (!monthsMap[monthKey]) {
      monthsMap[monthKey] = {
        monthKey,
        month: formatMonthForDisplay(monthKey),
        monthDate: new Date(monthKey + 'T00:00:00'),
      };
    }
    
    const channelName = resolveChannelName(row, channels);
    if (!channelName) return;
    
    monthsMap[monthKey][channelName] = (monthsMap[monthKey][channelName] || 0) + (row.total_revenue || 0);
  });

  const channelNames = channels.map(c => c.name);
  Object.values(monthsMap).forEach(monthData => {
    channelNames.forEach(channelName => {
      if (!(channelName in monthData)) {
        monthData[channelName] = 0;
      }
    });
  });

  // Convert to array and sort by monthKey (chronological)
  // Add total_value for each month (sum of all channel values)
  return Object.values(monthsMap)
    .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
    .map(({ monthKey, monthDate, ...rest }) => {
      // Calculate total_value as sum of all channel values for this month
      const totalValue = channelNames.reduce((sum, channelName) => {
        return sum + (rest[channelName] || 0);
      }, 0);
      
      return {
        ...rest,
        monthKey, // Keep for reference
        monthDate, // Keep for drilldown
        total_value: totalValue, // Total for label display
      };
    });
}

/**
 * Transform reservations_by_channel rows to chart data
 * @param {Array} reservationRows - Array of { month, channel_code, reservation_count }
 * @param {Array} channels - Array of { code, name, color_hex }
 * @returns {Array} - Chart data: [{ month, monthKey, channel1: val, channel2: val, ... }, ...]
 */
export function transformReservationsData(reservationRows, channels = []) {
  if (!reservationRows || reservationRows.length === 0) {
    return [];
  }

  // Build map of month -> { channel: value }
  const monthsMap = {};
  
  reservationRows.forEach((row) => {
    const monthKey = normalizeMonthKey(row.month);
    
    if (!monthsMap[monthKey]) {
      monthsMap[monthKey] = {
        monthKey,
        month: formatMonthForDisplay(monthKey),
        monthDate: new Date(monthKey + 'T00:00:00'),
      };
    }
    
    const channelName = resolveChannelName(row, channels);
    if (!channelName) return;
    
    // Accumulate count (in case of duplicates)
    monthsMap[monthKey][channelName] = (monthsMap[monthKey][channelName] || 0) + (row.reservation_count || 0);
  });

  // Ensure all channels have a value (0 if missing) for stable stacking
  const channelNames = channels.map(c => c.name);
  Object.values(monthsMap).forEach(monthData => {
    channelNames.forEach(channelName => {
      if (!(channelName in monthData)) {
        monthData[channelName] = 0;
      }
    });
  });

  // Convert to array and sort by monthKey (chronological)
  // Add total_value for each month (sum of all channel values)
  return Object.values(monthsMap)
    .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
    .map(({ monthKey, monthDate, ...rest }) => {
      // Calculate total_value as sum of all channel values for this month
      const totalValue = channelNames.reduce((sum, channelName) => {
        return sum + (rest[channelName] || 0);
      }, 0);
      
      return {
        ...rest,
        monthKey, // Keep for reference
        monthDate, // Keep for drilldown
        total_value: totalValue, // Total for label display
      };
    });
}
